import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { FileText, Check, Clock, AlertCircle, AlertTriangle, Loader2, Send, CheckCircle, Building, User, MessageSquare, Download, ChevronDown, ChevronUp, CreditCard, Truck } from 'lucide-react';
import { API_BASE_URL, quotesAPI, depositsAPI } from '../services/api';
import PayDepositModal from '../components/PayDepositModal';
import SignatureCapture from '../components/common/SignatureCapture';
import { formatMoney } from '../utils/format';
import { QuoteLineSections, SalesRepCard, AboutCard, PurchaseOrderField, RecurringPhrase } from '../components/quotes/QuotePresentation';
import { notifyError } from '../context/toastStore';

// Format currency
function formatCurrency(amount) {
  return formatMoney(amount || 0);
}

// Format date - handles date-only strings to avoid timezone shift
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  // Adding T12:00:00 to date-only strings prevents timezone shift
  const date = dateString.includes('T')
    ? new Date(dateString)
    : new Date(dateString + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function QuoteViewPage() {
  const { token } = useParams();
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showPayDeposit, setShowPayDeposit] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [showCheckConfirm, setShowCheckConfirm] = useState(false);
  const [checkSubmitting, setCheckSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    lineItems: true,
    terms: false,
    comments: false,
  });

  // Fetch quote data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['quote', token],
    queryFn: () => quotesAPI.view(token),
    retry: false,
  });

  const quote = data?.data?.quote;
  const session = data?.data?.session;
  const isAccepted = session?.status === 'accepted';

  // For accepted quotes, deposit/settings/companyInfo come directly from the
  // getQuoteByToken accepted response (keyed by opportunityId, not share-link token).
  // For non-accepted quotes with a deposit payment link, fetch by payment token.
  const acceptedDeposit = data?.data?.deposit || null;
  const deposit = acceptedDeposit;
  const depositsEnabled = data?.data?.depositsEnabled !== false;
  const paymentSettings = data?.data?.paymentSettings || null;
  const companyInfo = data?.data?.companyInfo || {};

  // Refetch helper for after payment / check confirmation
  const refetchDeposit = refetch;
  // Sections, totals, the account manager and the purchase order rule (services/quotes/presentation.js).
  const presentation = data?.data?.presentation || null;
  const totals = presentation?.totals || data?.data?.totals;
  const signatures = data?.data?.signatures || [];
  const comments = data?.data?.comments || [];
  const featureFlags = data?.data?.featureFlags || {};
  const termsAndConditions = data?.data?.termsAndConditions || '';
  const expirationDate = data?.data?.quote?.expirationDate; // Quote expiration from CW custom field
  const isExpired = data?.data?.quote?.isExpired || false; // Whether the quote is expired
  const depositAmount = data?.data?.quote?.depositAmount; // Deposit amount from QuoteWerks
  const paymentTerms = data?.data?.quote?.paymentTerms; // Payment terms from QuoteWerks
  const shippingMethod = data?.data?.quote?.shippingMethod; // Shipping method from QuoteWerks

  // Feature flags for this quote's company
  const canComment = featureFlags['feature.quote_comments'] !== false;

  // Handle error states
  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-neutral-600">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const errorData = error.response?.data;
    const errorMessage = errorData?.error || 'Failed to load quote';
    const additionalMessage = errorData?.message;
    const isExpired = error.response?.status === 410;
    const isInvalidated = isExpired && additionalMessage?.includes('newer link');

    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-soft p-8 max-w-md w-full text-center">
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
            isExpired ? 'bg-amber-100' : 'bg-red-100'
          }`}>
            {isExpired ? (
              <Clock className="w-8 h-8 text-amber-600" />
            ) : (
              <AlertCircle className="w-8 h-8 text-red-600" />
            )}
          </div>
          <h1 className="text-xl font-bold text-neutral-900 mb-2">
            {isInvalidated ? 'Link No Longer Valid' : isExpired ? 'Quote Expired' : 'Quote Not Found'}
          </h1>
          <p className="text-neutral-600 mb-4">{errorMessage}</p>
          {additionalMessage && (
            <p className="text-neutral-500 mb-4 text-sm">{additionalMessage}</p>
          )}
          <p className="text-sm text-neutral-500">
            Please contact your account representative for assistance.
          </p>
        </div>
      </div>
    );
  }

  // Accepted state
  if (isAccepted) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-soft p-8 max-w-lg w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-neutral-900 mb-2">Quote Accepted</h1>
          <p className="text-neutral-600 mb-4">
            This quote was accepted on {formatDate(session?.acceptedAt || data?.data?.acceptedAt)}
          </p>
          {(data?.data?.signerName || signatures[0]?.signer_name) && (
            <div className="text-sm text-neutral-500 mb-6">
              Signed by: {data?.data?.signerName || signatures[0]?.signer_name}
            </div>
          )}

          {/* Deposit Section */}
          {deposit && depositsEnabled && (
            <div className={`mt-6 p-4 rounded-lg border ${
              deposit.status === 'paid'
                ? 'bg-green-50 border-green-200'
                : deposit.status === 'waived'
                ? 'bg-neutral-50 border-neutral-200'
                : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <CreditCard className={`w-5 h-5 ${
                  deposit.status === 'paid'
                    ? 'text-green-600'
                    : deposit.status === 'waived'
                    ? 'text-neutral-600'
                    : 'text-amber-600'
                }`} />
                <span className={`font-semibold ${
                  deposit.status === 'paid'
                    ? 'text-green-800'
                    : deposit.status === 'waived'
                    ? 'text-neutral-800'
                    : 'text-amber-800'
                }`}>
                  {deposit.status === 'paid' ? 'Deposit Paid' :
                   deposit.status === 'waived' ? 'Deposit Waived' :
                   'Deposit Required'}
                </span>
              </div>

              {deposit.status === 'paid' ? (
                <>
                  <p className="text-green-700 mb-2">
                    Thank you! Your deposit of {formatCurrency(deposit.amountPaid)} has been received.
                  </p>
                  {deposit.payments && deposit.payments.length > 0 && (() => {
                    const p = deposit.payments[0];
                    const date = p.paymentDate || p.payment_date;
                    const source = p.paymentSource || p.payment_source;
                    const cardType = p.cardType || p.card_type;
                    const last4 = p.cardLastFour || p.card_last_four;
                    const isAch = source === 'one_time_ach' || cardType === 'ACH';
                    const method = isAch ? 'ACH' : (cardType || 'Card');
                    return (
                      <p className="text-sm text-green-600">
                        Paid on {formatDate(date)} via {method}{last4 ? ` ****${last4}` : ''}
                      </p>
                    );
                  })()}
                </>
              ) : deposit.status === 'waived' ? (
                <p className="text-neutral-700">
                  The deposit requirement has been waived.
                </p>
              ) : deposit.sendingCheck ? (
                <div className="text-left">
                  <p className="text-amber-700 font-medium mb-1">Check payment noted</p>
                  <p className="text-sm text-amber-600">
                    We've recorded that you'll be mailing a check. Your order will not be processed until the check is received and successfully deposited.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-amber-700 mb-4">
                    A deposit of {formatCurrency(deposit.depositAmount)} is required to proceed with this quote.
                    {deposit.status === 'partial' && (
                      <span className="block mt-1">
                        Remaining: {formatCurrency(deposit.amountRemaining)}
                      </span>
                    )}
                  </p>

                  {!showCheckConfirm ? (
                    <div className="flex flex-col gap-3">
                      <button onClick={() => setShowPayDeposit(true)} className="btn-primary">
                        <CreditCard className="w-4 h-4" />
                        Pay Deposit {formatCurrency(deposit.amountRemaining || deposit.depositAmount)}
                      </button>
                      <button
                        onClick={() => setShowCheckConfirm(true)}
                        className="text-sm text-amber-700 hover:text-amber-900 underline underline-offset-2"
                      >
                        I'll send a check instead
                      </button>
                    </div>
                  ) : (
                    <div className="text-left space-y-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div>
                        <p className="font-medium text-amber-900 mb-1">Mail your check to:</p>
                        {companyInfo.companyName && (
                          <p className="text-neutral-900 font-medium">{companyInfo.companyName}</p>
                        )}
                        {companyInfo.companyAddress ? (
                          <p className="text-neutral-700 whitespace-pre-line text-sm">{companyInfo.companyAddress}</p>
                        ) : (
                          <p className="text-neutral-500 text-sm italic">Contact us for mailing address</p>
                        )}
                        {companyInfo.supportEmail && (
                          <p className="text-sm text-neutral-600 mt-1">Questions? <a href={`mailto:${companyInfo.supportEmail}`} className="text-primary-600 underline">{companyInfo.supportEmail}</a></p>
                        )}
                      </div>
                      <div className="bg-amber-100 border border-amber-300 rounded p-3">
                        <p className="text-sm text-amber-900 font-medium">
                          ⚠️ Please make your check payable to {companyInfo.companyName || 'us'} for {formatCurrency(deposit.amountRemaining || deposit.depositAmount)}.
                        </p>
                        <p className="text-sm text-amber-800 mt-1">
                          Your order will not be placed until your check is received and successfully deposited.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={async () => {
                            setCheckSubmitting(true);
                            try {
                              await depositsAPI.markSendingCheck(deposit.paymentToken);
                              await refetchDeposit();
                            } catch (e) {
                              console.error(e);
                            } finally {
                              setCheckSubmitting(false);
                            }
                          }}
                          disabled={checkSubmitting}
                          className="btn-primary flex-1"
                        >
                          {checkSubmitting ? 'Saving...' : 'Confirm — I\'ll send a check'}
                        </button>
                        <button
                          onClick={() => setShowCheckConfirm(false)}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="mt-6 text-sm text-neutral-400">
            Thank you for your business. Please contact us if you have any questions.
          </div>
        </div>

        {/* Pay Deposit Modal for shared link */}
        {showPayDeposit && deposit && (
          <PayDepositModal
            isOpen={showPayDeposit}
            onClose={() => setShowPayDeposit(false)}
            deposit={deposit}
            quoteName={data?.data?.signerName ? `Accepted by ${data.data.signerName}` : 'Quote'}
            isPublic={true}
            paymentToken={deposit.paymentToken}
            paymentSettings={paymentSettings}
            gateway={data?.data?.gateway || null}
            onSuccess={() => {
              setShowPayDeposit(false);
              refetch();
            }}
          />
        )}
      </div>
    );
  }

  // Quote expired state (based on custom field, not link expiration)
  if (isExpired) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-soft p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-neutral-900 mb-2">Quote Has Expired</h1>
          <p className="text-neutral-600 mb-4">
            This quote expired on {formatDate(expirationDate)} and can no longer be accepted.
          </p>
          <p className="text-sm text-neutral-500">
            Please contact your account representative for an updated quote.
          </p>
        </div>
      </div>
    );
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 text-sm text-neutral-600 mb-1">
            <Building className="w-4 h-4" />
            <span>{quote?.company?.name}</span>
          </div>
          <h1 className="text-2xl font-bold text-neutral-900">{quote?.name || 'Quote'}</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Expired Warning Banner */}
        {isExpired && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-red-800 font-medium">This quote has expired</p>
              <p className="text-red-700 text-sm">
                This quote expired on {formatDate(expirationDate)} and can no longer be accepted.
                Please contact us for an updated quote.
              </p>
            </div>
          </div>
        )}

        {/* Quote Info */}
        <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Building className="w-5 h-5 text-neutral-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-sm text-neutral-600">Company</div>
                <div className="font-medium text-neutral-900">{quote?.company?.name || 'N/A'}</div>
              </div>
            </div>
            {quote?.contact && (
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-neutral-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm text-neutral-600">Contact</div>
                  <div className="font-medium text-neutral-900">{quote.contact.name}</div>
                  {quote.contact.email && (
                    <div className="text-sm text-neutral-600">{quote.contact.email}</div>
                  )}
                </div>
              </div>
            )}
            {expirationDate && (
              <div className="flex items-start gap-3">
                <Clock className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isExpired ? 'text-red-500' : 'text-neutral-400'}`} />
                <div>
                  <div className="text-sm text-neutral-600">Quote Expires</div>
                  <div className={`font-medium ${isExpired ? 'text-red-600' : 'text-neutral-900'}`}>
                    {formatDate(expirationDate)}
                    {isExpired && <span className="ml-2 text-xs font-semibold text-red-500">(EXPIRED)</span>}
                  </div>
                </div>
              </div>
            )}
            {paymentTerms && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-neutral-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm text-neutral-600">Payment Terms</div>
                  <div className="font-medium text-neutral-900">{paymentTerms}</div>
                </div>
              </div>
            )}
            {shippingMethod && (
              <div className="flex items-start gap-3">
                <Truck className="w-5 h-5 text-neutral-400 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm text-neutral-600">Shipping Method</div>
                  <div className="font-medium text-neutral-900">{shippingMethod}</div>
                </div>
              </div>
            )}
          </div>
          {quote?.description && (
            <div className="mt-6 pt-6 border-t border-neutral-200">
              <div className="text-sm text-neutral-600 mb-2">Description</div>
              <p className="text-neutral-800 whitespace-pre-wrap">{quote.description}</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <AboutCard about={presentation?.about} />

            {/* Line Items */}
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h2 className="font-semibold text-neutral-900">Line Items</h2>
              </div>
              <QuoteLineSections presentation={presentation} depositAmount={depositAmount} />
            </div>

            {/* Terms and Conditions */}
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              <button
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50"
                onClick={() => toggleSection('terms')}
              >
                <h2 className="font-semibold text-neutral-900">Terms and Conditions</h2>
                {expandedSections.terms ? (
                  <ChevronUp className="w-5 h-5 text-neutral-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-neutral-400" />
                )}
              </button>

              {expandedSections.terms && (
                <div className="border-t border-neutral-200 px-6 py-4">
                  <div className="prose prose-sm text-neutral-600 whitespace-pre-wrap">
                    {termsAndConditions || 'No terms and conditions specified.'}
                  </div>
                </div>
              )}
            </div>

            {/* Comments Section */}
            {canComment && (
              <div className="bg-white rounded-xl shadow-soft overflow-hidden">
                <button
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50"
                  onClick={() => toggleSection('comments')}
                >
                  <h2 className="font-semibold text-neutral-900 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Questions & Comments
                  </h2>
                  {expandedSections.comments ? (
                    <ChevronUp className="w-5 h-5 text-neutral-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-neutral-400" />
                  )}
                </button>

                {expandedSections.comments && (
                  <div className="border-t border-neutral-200 p-6">
                    <CommentSection token={token} comments={comments} onCommentAdded={refetch} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <SalesRepCard rep={presentation?.salesRep} />
            <div className="bg-white rounded-xl shadow-soft p-6 sticky top-6">
              <div className="text-center mb-5">
                <div className="text-sm text-neutral-600 mb-1">{totals?.recurring > 0 ? 'Due now' : 'Quote Total'}</div>
                <div className="text-3xl font-bold text-primary-600">{formatCurrency(totals?.total)}</div>
                {totals?.recurring > 0 && (
                  <div className="mt-1 text-sm text-neutral-600">
                    plus <RecurringPhrase totals={totals} />
                  </div>
                )}
                {depositAmount > 0 && (
                  <div className="mt-2 text-sm text-amber-700 font-medium">
                    Deposit required: {formatCurrency(depositAmount)}
                  </div>
                )}
              </div>

              <button
                className="btn-primary w-full"
                onClick={() => setShowSignatureModal(true)}
              >
                <Check className="w-4 h-4" /> Accept Quote
              </button>

              <button
                className="btn-secondary w-full mt-2"
                disabled={pdfDownloading}
                onClick={async () => {
                  setPdfDownloading(true);
                  try {
                    const response = await fetch(`${API_BASE_URL}/quotes/view/${token}/pdf`);
                    if (!response.ok) throw new Error('Failed to download PDF');
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${quote?.name || 'quote'}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    notifyError('The PDF could not be downloaded.', err);
                  } finally {
                    setPdfDownloading(false);
                  }
                }}
              >
                {pdfDownloading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Downloading...</>
                ) : (
                  <><Download className="w-4 h-4" /> Download PDF</>
                )}
              </button>

              <p className="text-xs text-neutral-500 text-center mt-3">
                By clicking "Accept Quote", you agree to the terms and conditions above.
              </p>

              {(expirationDate || session?.expiresAt) && (
                <div className="mt-4 pt-4 border-t border-neutral-200 flex items-center gap-2 text-xs text-neutral-500">
                  <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                  {expirationDate ? (
                    <span>This quote expires {formatDate(expirationDate)}</span>
                  ) : (
                    <span>This quote expires {formatDate(session.expiresAt)}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          token={token}
          onClose={() => setShowSignatureModal(false)}
          onSuccess={() => {
            setShowSignatureModal(false);
            refetch();
          }}
          quoteName={quote?.name}
          total={totals?.total}
          poMode={presentation?.poField || 'off'}
        />
      )}
    </div>
  );
}

// Format comment date
function formatCommentDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Comment Section Component
function CommentSection({ token, comments = [], onCommentAdded }) {
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => quotesAPI.addComment(token, data),
    onSuccess: () => {
      setMessage('');
      if (onCommentAdded) onCommentAdded();
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!message.trim() || !name.trim() || !email.trim()) return;
    mutation.mutate({ message, authorName: name, authorEmail: email });
  };

  // Organize comments - top-level first, then replies nested
  const topLevelComments = comments.filter(c => !c.parent_id);
  const getReplies = (parentId) => comments.filter(c => c.parent_id === parentId);

  return (
    <div className="space-y-6">
      {/* Display existing comments */}
      {comments.length > 0 && (
        <div className="space-y-4 mb-6">
          {topLevelComments.map((comment) => (
            <div key={comment.id} className="space-y-3">
              {/* Main comment */}
              <div className={`p-4 rounded-lg ${
                comment.author_type === 'internal'
                  ? 'bg-primary-50 border border-primary-100 ml-8'
                  : 'bg-neutral-50 border border-neutral-100'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {comment.author_type === 'internal' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                      <Building className="w-3 h-3" />
                      {comment.author_name || 'Staff'}
                    </span>
                  ) : (
                    <>
                      <div className="w-6 h-6 bg-neutral-200 rounded-full flex items-center justify-center">
                        <User className="w-3 h-3 text-neutral-500" />
                      </div>
                      <span className="font-medium text-sm text-neutral-900">
                        {comment.author_name || 'Anonymous'}
                      </span>
                    </>
                  )}
                  <span className="text-xs text-neutral-500">
                    {formatCommentDate(comment.created_at)}
                  </span>
                </div>
                <p className="text-sm text-neutral-700 whitespace-pre-wrap">{comment.message}</p>
              </div>

              {/* Replies to this comment */}
              {getReplies(comment.id).map((reply) => (
                <div key={reply.id} className={`p-4 rounded-lg ml-8 ${
                  reply.author_type === 'internal'
                    ? 'bg-primary-50 border border-primary-100'
                    : 'bg-neutral-50 border border-neutral-100'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {reply.author_type === 'internal' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                        <Building className="w-3 h-3" />
                        Staff
                      </span>
                    ) : (
                      <div className="w-6 h-6 bg-neutral-200 rounded-full flex items-center justify-center">
                        <User className="w-3 h-3 text-neutral-500" />
                      </div>
                    )}
                    <span className="font-medium text-sm text-neutral-900">
                      {reply.author_name || 'Anonymous'}
                    </span>
                    <span className="text-xs text-neutral-500">
                      {formatCommentDate(reply.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-700 whitespace-pre-wrap">{reply.message}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Add new comment form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Your Name</label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label">Your Email</label>
            <input
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
        </div>
        <div>
          <label className="label">Message</label>
          <textarea
            className="input min-h-[100px]"
            placeholder="Ask a question or leave a comment..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          className="btn-primary"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4" /> Send Message
            </>
          )}
        </button>
        {mutation.isSuccess && (
          <p className="text-sm text-green-600">Message sent successfully!</p>
        )}
      </form>
    </div>
  );
}

// Signature Modal Component
function SignatureModal({ token, onClose, onSuccess, quoteName, total, poMode = 'off' }) {
  const [signerName, setSignerName] = useState('');
  const [customerPO, setCustomerPO] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [signature, setSignature] = useState(null);
  const onSignature = useCallback((value) => setSignature(value), []);

  const mutation = useMutation({
    mutationFn: (data) => quotesAPI.accept(token, data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to accept quote');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!signerName || !signerEmail) {
      setError('Please provide your name and email');
      return;
    }

    if (!acceptedTerms) {
      setError('You must accept the terms and conditions');
      return;
    }

    if (poMode === 'required' && !customerPO.trim()) {
      setError('Please enter your purchase order number');
      return;
    }

    if (!signature?.isValid) {
      setError(signature?.type === 'drawn' ? 'Please draw your signature' : 'Please type your signature');
      return;
    }

    mutation.mutate({
      signerName,
      signerEmail,
      signerTitle: signerTitle || undefined,
      signatureType: signature.type,
      signatureData: signature.data,
      acceptedTerms: true,
      ...(poMode !== 'off' && customerPO.trim() ? { customerPO: customerPO.trim() } : {}),
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-900">Accept Quote</h3>
          <p className="text-sm text-neutral-600">
            {quoteName} - {formatCurrency(total)}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Full Name *</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Email *</label>
              <input
                type="email"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Title (optional)</label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g., CEO, IT Director"
              value={signerTitle}
              onChange={(e) => setSignerTitle(e.target.value)}
            />
          </div>

          <PurchaseOrderField
            mode={poMode}
            value={customerPO}
            onChange={setCustomerPO}
            inputClassName="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-white text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />

          <div>
            <label className="label">Signature</label>
            <SignatureCapture onChange={onSignature} />
          </div>

          {/* Terms Acceptance */}
          <div className="border-t border-neutral-200 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border-neutral-300 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-600">
                {/* Word for word what the server stores as consent (QUOTE_CONSENT.shared in
                    services/contracts/signing.service.js); change both together. */}
                I have read and agree to the terms and conditions. By signing, I confirm that I am
                authorized to accept this quote on behalf of my organization and understand that
                this constitutes a legally binding agreement. I agree to sign electronically, and my
                electronic signature has the same effect as my handwritten signature.
              </span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Processing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" /> Accept & Sign
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
